import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../utils/api';

interface BotStatus {
  initialized: boolean;
  tokenConfigured: boolean;
  pollingActive: boolean;
  status: 'RUNNING' | 'NOT_INITIALIZED' | 'NO_TOKEN';
  botInfo: {
    username?: string;
    firstName?: string;
    id?: number;
  } | null;
}

interface BotStats {
  total: number;
  incoming: number;
  outgoing: number;
  byType: Array<{ type: string; count: number }>;
  byStatus: Array<{ status: string; count: number }>;
}

const TelegramSettings: React.FC = () => {
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);
  const [botStats, setBotStats] = useState<BotStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState(false);
  const [restartMessage, setRestartMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchBotStatus = useCallback(async () => {
    try {
      const status = await apiGet<BotStatus>('/telegram-messages/bot-status');
      setBotStatus(status);
    } catch (error) {
      console.error('Ошибка загрузки статуса бота:', error);
    }
  }, []);

  const fetchBotStats = useCallback(async () => {
    try {
      const stats = await apiGet<BotStats>('/telegram-messages/stats');
      setBotStats(stats);
    } catch (error) {
      console.error('Ошибка загрузки статистики бота:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchBotStatus(), fetchBotStats()]);
    setLoading(false);
  }, [fetchBotStatus, fetchBotStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRestart = async () => {
    if (!window.confirm('Вы уверены, что хотите перезапустить бота?')) {
      return;
    }

    setRestarting(true);
    setRestartMessage(null);

    try {
      const result = await apiPost<{ success: boolean; message: string }>('/telegram-messages/bot-restart', {});
      
      if (result.success) {
        setRestartMessage({ type: 'success', text: result.message });
        // Обновляем статус после перезапуска
        setTimeout(() => {
          fetchBotStatus();
        }, 2000);
      } else {
        setRestartMessage({ type: 'error', text: result.message });
      }
    } catch (error: any) {
      console.error('Ошибка перезапуска бота:', error);
      setRestartMessage({ 
        type: 'error', 
        text: error.message || 'Неизвестная ошибка при перезапуске бота' 
      });
    } finally {
      setRestarting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ✅ Работает
          </span>
        );
      case 'NOT_INITIALIZED':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            ⚠️ Не инициализирован
          </span>
        );
      case 'NO_TOKEN':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            ❌ Токен не настроен
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            ❓ Неизвестно
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Настройки Telegram бота</h1>
        <p className="mt-0.5 text-xs text-gray-500">
          Управление настройками и статусом Telegram бота
        </p>
      </div>

      {/* Статус бота */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Статус бота</h2>
        
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Статус:</span>
            {botStatus && getStatusBadge(botStatus.status)}
          </div>

          {botStatus?.botInfo && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Username:</span>
                <span className="text-xs text-gray-900">@{botStatus.botInfo.username}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Имя:</span>
                <span className="text-xs text-gray-900">{botStatus.botInfo.firstName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">ID:</span>
                <span className="text-xs text-gray-900">{botStatus.botInfo.id}</span>
              </div>
            </>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Токен настроен:</span>
            <span className={`text-xs ${botStatus?.tokenConfigured ? 'text-green-600' : 'text-red-600'}`}>
              {botStatus?.tokenConfigured ? '✅ Да' : '❌ Нет'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Polling активен:</span>
            <span className={`text-xs ${botStatus?.pollingActive ? 'text-green-600' : 'text-red-600'}`}>
              {botStatus?.pollingActive ? '✅ Да' : '❌ Нет'}
            </span>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={handleRestart}
            disabled={restarting || !botStatus?.tokenConfigured}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {restarting ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Перезапуск...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Перезапустить бота
              </>
            )}
          </button>

          {restartMessage && (
            <div className={`mt-2 p-2 text-xs rounded-lg ${
              restartMessage.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {restartMessage.text}
            </div>
          )}
        </div>
      </div>

      {/* Статистика */}
      {botStats && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Статистика</h2>
          
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-0.5">Всего сообщений</div>
              <div className="text-xl font-bold text-gray-900">{botStats.total}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-xs text-blue-600 mb-0.5">Входящие</div>
              <div className="text-xl font-bold text-blue-900">{botStats.incoming}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-xs text-green-600 mb-0.5">Исходящие</div>
              <div className="text-xl font-bold text-green-900">{botStats.outgoing}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {botStats.byType.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-700 mb-2">По типам сообщений</h3>
                <div className="space-y-1">
                  {botStats.byType.map((item) => (
                    <div key={item.type} className="flex items-center justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-700">{item.type}</span>
                      <span className="text-xs font-medium text-gray-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {botStats.byStatus.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-700 mb-2">По статусам</h3>
                <div className="space-y-1">
                  {botStats.byStatus.map((item) => (
                    <div key={item.status} className="flex items-center justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-700">{item.status}</span>
                      <span className="text-xs font-medium text-gray-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Информация */}
      <div className="mt-4 bg-blue-50 rounded-lg border border-blue-200 p-3">
        <h3 className="text-xs font-semibold text-blue-900 mb-1.5">ℹ️ Информация</h3>
        <ul className="text-xs text-blue-800 space-y-0.5">
          <li>• Бот работает только в приватных чатах (не отвечает в группах)</li>
          <li>• Все сообщения логируются в базу данных</li>
          <li>• Токен бота настраивается через переменную окружения TELEGRAM_BOT_TOKEN</li>
          <li>• Для получения токена обратитесь к @BotFather в Telegram</li>
        </ul>
      </div>
    </div>
  );
};

export default TelegramSettings;

